import { Check, ArrowRight } from 'lucide-react';
import type { SavingsPackage } from '@/shared/types';


interface SavingsPackageCardProps {
  package: SavingsPackage;
  isPopular?: boolean;
  onSelect?: () => void;
  buttonText?: string;
}

export default function SavingsPackageCard({
  package: pkg,
  isPopular = false,
  onSelect,
  buttonText = "Select Package"
}: SavingsPackageCardProps) {
  return (
    <div className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-2xl hover:scale-105 ${isPopular
      ? 'border-blue-500 ring-4 ring-blue-500/20'
      : 'border-gray-200 hover:border-blue-300'
      }`}>
      {isPopular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
            Most Popular
          </span>
        </div>
      )}

      <div className="p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {pkg.name}
          </h3>
          <p className="text-gray-600 text-sm">
            {pkg.description}
          </p>
        </div>

        {/* Price */}
        <div className="text-center mb-8">
          <div className="flex items-baseline justify-center">
            <span className="text-4xl font-bold text-gray-900">
              ${pkg.minDeposit.toLocaleString()}
            </span><br />

          </div>
          <span className="text-gray-600 ml-2">minimum</span>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-gray-700">
              <span className="font-semibold">{pkg.monthlyReturn}%</span> Monthly Returns
            </span>
          </div>
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-gray-700">
              <span className="font-semibold">{pkg.referralBonus}%</span> Referral Bonus
            </span>
          </div>
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-gray-700">Secure & Encrypted</span>
          </div>
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-gray-700">24/7 Support</span>
          </div>
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
            <span className="text-gray-700">Easy Withdrawals</span>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={onSelect}
          className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 ${isPopular
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
            : 'bg-gray-900 text-white hover:bg-gray-800'
            }`}
        >
          <span>{buttonText}</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -z-10 transform translate-x-16 -translate-y-16 opacity-50"></div>
    </div >
  );
}
